import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'

import { Box, Grid, ProgressBar, Tabs, Text } from '@factorialco/deprecated-design-system'
import { ProjectManagementProjectDetailsPageSpendingPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { InsightCard } from 'modules/project_management/components/InsightCard'
import { ProjectPurchaseInvoiceDataTable } from 'modules/project_management/components/tables/ProjectPurchaseInvoiceDataTable'
import ProjectSpendingDataCollection from 'modules/project_management/components/tables/ProjectSpendingDataCollection'
import ProjectSpendingDataTable from 'modules/project_management/components/tables/ProjectSpendingDataTable'
import ProjectSpendingDetailPanel from 'modules/project_management/components/tables/ProjectSpendingDataTable/TableBody/ProjectSpendingDetailPanel'
import { UpsellProjectSpendingMessage } from 'modules/project_management/components/upsells/UpsellProjectSpendingMessage'

import { IProjectSpendingPanelData } from './types'

const SpendingPage = () => {
  const { id } = useParams()
  const projectId = Number(id)
  const { company } = useCurrentSession()
  const { hasExpenses, hasCost } = useOutletContext<{
    hasExpenses: boolean
    hasCost: boolean
  }>()

  const hasCostAndExpenses = hasCost && hasExpenses

  const { data } = useQuery(ProjectManagementProjectDetailsPageSpendingPageDocument, {
    variables: { companyId: company.id, projectId, includeInsights: hasCostAndExpenses },
  })
  const spendingInsight = data?.projectManagement.spendingInsightsConnection?.edges[0]?.node
  const totalCount = spendingInsight?.totalCount || 0
  const approvedTotalCount = spendingInsight?.approvedTotalCount || 0
  const dataCollectionsEnabled =
    data?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_data_collections'
    )?.enabled ?? false

  const purchaseInvoicesEnabled =
    data?.features.featuresConnection.nodes.find(
      (node) => node.name === 'dev_projects_purchase_invoices'
    )?.enabled ?? false

  const project = data?.projectManagement.project

  useEffect(() => {
    track(trackingEvents.projectManagement.spending.visitSpendingSection)
  }, [])

  const projectLegalEntityCurrency = project?.legalEntity.currency || 'EUR'
  const [showSpendingDetailPanel, setShowSpendingDetailPanel] = useState<boolean>(false)
  const [projectSpendingPanelData, setProjectSpendingPanelData] =
    useState<IProjectSpendingPanelData>({
      effectiveOn: '',
      employeeName: '',
      exchangeRate: null,
      expensableId: 0,
      legalEntityLegalName: null,
      legalEntityAmountCents: null,
      legalEntityAmountCurrency: null,
      originalAmountCents: 0,
      originalAmountCurrency: projectLegalEntityCurrency,
    })

  const [selectedTab, setSelectedTab] = useState<'expenses' | 'purchase_invoices'>('expenses')

  const tabs = [
    {
      id: 'expenses',
      active: true,
      label: i18n.t('project_management.spending.tabs.expenses'),
    },
    {
      id: 'purchase_invoices',
      active: false,
      label: i18n.t('project_management.spending.tabs.purchase_invoices'),
    },
  ]

  if (!project) return null

  const openSidePanel = (newData: IProjectSpendingPanelData) => {
    setShowSpendingDetailPanel(true)
    setProjectSpendingPanelData(newData)
  }

  return (
    <Box gap="s24">
      <Grid variant="fixed">
        <Box gridColSpan={{ xxs: 12, s: 4 }}>
          <InsightCard.Counter
            title={i18n.t('project_management.spending.insights.total_expenses')}
            totalCount={totalCount}
          />
        </Box>
        <Box gridColSpan={{ xxs: 12, s: 4 }}>
          <InsightCard
            title={i18n.t('project_management.spending.insights.approved_expenses')}
            titleGap="s0"
          >
            <Box gap="s4">
              <Text.Header2 weight="semibold">{`${approvedTotalCount} / ${totalCount}`}</Text.Header2>
              <ProgressBar
                amount={approvedTotalCount}
                total={totalCount}
                progressColor="viridian1000"
              />
            </Box>
          </InsightCard>
        </Box>
        <Box gridColSpan={{ xxs: 12, s: 4 }}>
          <InsightCard.Cost
            totalCost={hasCostAndExpenses ? Number(project.spendingCostCents) : 0}
            currency={projectLegalEntityCurrency}
          />
        </Box>
      </Grid>
      {dataCollectionsEnabled ? (
        hasCostAndExpenses ? (
          <ProjectSpendingDataCollection
            openSidePanel={openSidePanel}
            projectId={project.id}
            projectCurrency={projectLegalEntityCurrency}
          />
        ) : (
          <Box paddingTop="s24">
            <UpsellProjectSpendingMessage hasCost={hasCost} hasExpenses={hasExpenses} />
          </Box>
        )
      ) : purchaseInvoicesEnabled ? (
        <Tabs
          currentId={selectedTab}
          onTabClicked={(tabId: 'expenses' | 'purchase_invoices') => {
            setSelectedTab(tabId)
          }}
        >
          {tabs.map(({ id: tabId, label }) => (
            <Tabs.Tab key={tabId} id={tabId} label={label}>
              {selectedTab === 'expenses' ? (
                <ProjectSpendingDataTable
                  hasCost={hasCost}
                  hasExpenses={hasExpenses}
                  openSidePanel={openSidePanel}
                  projectId={project.id}
                  projectCurrency={projectLegalEntityCurrency}
                />
              ) : (
                <ProjectPurchaseInvoiceDataTable projectId={project.id} />
              )}
            </Tabs.Tab>
          ))}
        </Tabs>
      ) : (
        <ProjectSpendingDataTable
          hasCost={hasCost}
          hasExpenses={hasExpenses}
          openSidePanel={openSidePanel}
          projectId={project.id}
          projectCurrency={projectLegalEntityCurrency}
          title={i18n.t('project_management.spending.table.title')}
        />
      )}
      <ProjectSpendingDetailPanel
        onClose={() => setShowSpendingDetailPanel(false)}
        projectSpendingPanelData={projectSpendingPanelData}
        showSpendingDetailPanel={showSpendingDetailPanel}
      />
    </Box>
  )
}

export default SpendingPage
